if (!customElements.get('product-combined')) {
  customElements.define(
    'product-combined',
    class ProductCombined extends HTMLElement {
      constructor() {
        super();
        this.config = JSON.parse(
          this.querySelector('[data-combined-product-config]').textContent
        );
        this.currentSize = this.config.defaultSize;
        this.currentFragrance = this.config.defaultFragrance;
        this.currentPlan = 'onetime';
        this.sourceSectionId = this.closest('.shopify-section')?.id || null;
        this.shouldSyncURL = false;
      }

      connectedCallback() {
        // Detect initial product from URL
        this.detectFromURL();
        this.setInitialPlanSelection();

        // Bind size pills
        this.querySelectorAll('[data-selector="size"]').forEach((btn) => {
          btn.addEventListener('click', (e) => this.onSizeChange(e));
        });

        // Bind fragrance pills
        this.querySelectorAll('[data-selector="fragrance"]').forEach((btn) => {
          btn.addEventListener('click', (e) => this.onFragranceChange(e));
        });

        // Bind plan options
        this.querySelectorAll('.combined-product__plan-option').forEach(
          (option) => {
            option.addEventListener('click', (e) => this.onPlanChange(e));
          }
        );
        this.querySelectorAll('input[type="radio"][name="combined-plan"]').forEach(
          (radio) => {
            radio.addEventListener('change', (e) => this.onPlanRadioChange(e));
          }
        );

        // Bind delivery interval dropdowns
        this.querySelectorAll('.combined-product__delivery-dropdown').forEach(
          (select) => {
            select.addEventListener('change', (e) => this.onDeliveryChange(e));
            // Stop click from bubbling to plan label (prevents toggling plan off)
            select.addEventListener('click', (e) => e.stopPropagation());
          }
        );

        this.querySelectorAll('.combined-product__read-more-btn').forEach((btn) => {
          btn.addEventListener('click', (e) => this.onReadMoreToggle(e));
        });

        if (!this._readMoreResizeBound) {
          this._readMoreResizeBound = true;
          window.addEventListener('resize', () => this.syncReadMoreButtons());
        }

        // Bind info modals (size + fragrance)
        this.initInfoModal('size');
        this.initInfoModal('fragrance');

        // Close modals on Escape
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this.closeAllModals();
        });

        // Set initial state
        this.updatePills('size', this.currentSize);
        this.updatePills('fragrance', this.currentFragrance);
        this.updatePlanUI(this.currentPlan);
        this.updateView();

        // Init sticky ATC bar
        this.initStickyATC();
      }

      get currentKey() {
        return `${this.currentFragrance}-${this.currentSize}`;
      }

      detectFromURL() {
        const path = window.location.pathname;
        if (!path.startsWith('/products/')) return;
        for (const [key, data] of Object.entries(this.config.products)) {
          if (data.handle && path.includes(data.handle)) {
            const parts = key.split('-');
            this.currentFragrance = parts[0];
            this.currentSize = parts[1];
            this.shouldSyncURL = true;
            break;
          }
        }
      }

      setInitialPlanSelection() {
        const productData = this.config.products[this.currentKey];
        const shouldAutoSelectSubscribe =
          this.currentSize === '100ml' && !!productData?.hasSellingPlan;
        this.currentPlan = shouldAutoSelectSubscribe ? 'subscribe' : 'onetime';
      }

      onSizeChange(e) {
        const newSize = e.currentTarget.dataset.value;
        if (newSize === this.currentSize) return;
        const previousHeight = this.offsetHeight;
        this.currentSize = newSize;

        // Reset plan to onetime when switching to 20ml
        if (newSize === '20ml') {
          this.currentPlan = 'onetime';
          this.updatePlanUI('onetime');
        }

        this.updatePills('size', newSize);
        this.updateView();
        this.emitCombinedVariantLayoutChange(previousHeight);
      }

      onFragranceChange(e) {
        const newFragrance = e.currentTarget.dataset.value;
        if (newFragrance === this.currentFragrance) return;
        const previousHeight = this.offsetHeight;
        this.currentFragrance = newFragrance;
        this.updatePills('fragrance', newFragrance);
        this.updateView();
        this.emitCombinedVariantLayoutChange(previousHeight);
      }

      onPlanChange(e) {
        // Labels fire click twice (label + radio bubble), ignore the radio bubble
        if (e.target.tagName === 'INPUT') return;
        const option = e.currentTarget;
        const radio = option.querySelector('input[type="radio"]');
        if (!radio) return;
        radio.checked = true;
        this.currentPlan = radio.value;
        this.updatePlanUI(radio.value);
        this.updateSellingPlan();
        this.syncStickyPlanState();
        this.updateStickyPrice();
      }

      onPlanRadioChange(e) {
        const radio = e.currentTarget;
        if (!radio.checked) return;
        this.currentPlan = radio.value;
        this.updatePlanUI(radio.value);
        this.updateSellingPlan();
        this.syncStickyPlanState();
        this.updateStickyPrice();
      }

      updatePills(selector, value) {
        this.querySelectorAll(`[data-selector="${selector}"]`).forEach(
          (btn) => {
            btn.classList.toggle(
              'combined-product__pill--active',
              btn.dataset.value === value
            );
          }
        );
      }

      updatePlanUI(plan) {
        this.querySelectorAll('.combined-product__plan-option').forEach(
          (option) => {
            const radio = option.querySelector('input[type="radio"]');
            if (radio) {
              radio.checked = radio.value === plan;
              option.classList.toggle(
                'combined-product__plan-option--active',
                radio.value === plan
              );
            }
          }
        );

        this.querySelectorAll('[data-subscription-details]').forEach((el) => {
          el.hidden = plan !== 'subscribe';
        });
      }

      updateBadge() {
        const badge = this.querySelector('[data-badge]');
        if (!badge) return;
        const text = this.currentSize === '100ml'
          ? badge.dataset.badgeLarge
          : badge.dataset.badgeSmall;
        if (text) {
          badge.textContent = text;
          badge.hidden = false;
        } else {
          badge.hidden = true;
        }
      }

      updateView() {
        const key = this.currentKey;

        // Toggle all panels by data-product-key
        this.querySelectorAll('[data-product-key]').forEach((panel) => {
          const matches = panel.dataset.productKey === key;
          panel.hidden = !matches;
        });

        // Toggle plan sections: full plans on 100ml, one-time only on 20ml
        const subscriptionPlanToggle = this.querySelector(
          '[data-plan-subscription-toggle]'
        );
        if (subscriptionPlanToggle) {
          subscriptionPlanToggle.hidden = this.currentSize !== '100ml';
        }
        const oneTimePlanToggle = this.querySelector(
          '[data-plan-onetime-toggle]'
        );
        if (oneTimePlanToggle) {
          oneTimePlanToggle.hidden = this.currentSize !== '20ml';
        }

        // Toggle + swap badge per size and plan
        this.updateBadge();

        // Update selling plan input state
        this.updateSellingPlan();
        this.updateSubscriptionDetails();

        // Update URL
        this.updateURL();

        // Re-init gallery Swiper for newly visible panel
        this.reinitGallery(key);

        // Sync sticky bar state
        this.syncStickyDropdowns();
        this.updateStickyPrice();
        this.observeActiveBuyButton();
        this.syncJudgeMeWidgets();
        this.syncReadMoreButtons();
      }

      syncReadMoreButtons() {
        requestAnimationFrame(() => {
          this.querySelectorAll('.combined-product__read-more-btn').forEach((btn) => {
            const wrapper = btn.closest('[data-product-key]') || btn.parentElement;
            if (wrapper?.hidden) return;

            const desc = wrapper.querySelector('.product__description');
            if (!desc || desc.classList.contains('combined-product__description--expanded')) return;

            desc.classList.add('combined-product__description--truncated');
            const overflows = desc.scrollHeight > desc.clientHeight + 2;

            btn.classList.toggle('combined-product__read-more-btn--visible', overflows);
            if (!overflows) {
              desc.classList.remove('combined-product__description--truncated');
              btn.textContent = 'Read more';
            }
          });
        });
      }

      emitCombinedVariantLayoutChange(previousHeight) {
        const beforeHeight = typeof previousHeight === 'number' ? previousHeight : this.offsetHeight;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const afterHeight = this.offsetHeight;
            const heightDelta = afterHeight - beforeHeight;
            const layoutChanged = Math.abs(heightDelta) > 1;

            if (!layoutChanged) return;

            window.dispatchEvent(
              new CustomEvent('kizen:combined-variant-change', {
                detail: {
                  size: this.currentSize,
                  fragrance: this.currentFragrance,
                  sourceSectionId: this.sourceSectionId,
                  layoutChanged: true,
                  heightBefore: beforeHeight,
                  heightAfter: afterHeight,
                  heightDelta: heightDelta
                }
              })
            );
          });
        });
      }

      syncJudgeMeWidgets() {
        const productId = this.config.products[this.currentKey]?.id;
        if (productId == null || typeof window.updateJudgeMeWidgets !== 'function') return;
        window.updateJudgeMeWidgets(productId);
      }

      getDeliveryData() {
        const select = this.querySelector('.combined-product__delivery-dropdown');
        if (!select) return null;
        const opt = select.options[select.selectedIndex];
        const fragrance = this.currentFragrance;
        return {
          planId: opt.dataset[`plan${fragrance.charAt(0).toUpperCase() + fragrance.slice(1)}`],
          price: parseInt(opt.dataset[`price${fragrance.charAt(0).toUpperCase() + fragrance.slice(1)}`], 10)
        };
      }

      onDeliveryChange(e) {
        const data = this.getDeliveryData();
        if (!data) return;
        const key = this.currentKey;

        // Update the subscribe price display
        const priceSpan = this.querySelector(
          `.combined-product__plan-option:last-child .combined-product__plan-prices [data-product-key="${key}"] .combined-product__plan-price-current`
        );
        if (priceSpan && data.price) {
          priceSpan.textContent = Shopify.formatMoney ? Shopify.formatMoney(data.price) : '$' + (data.price / 100).toFixed(2);
        }

        // Update the selling plan input
        this.updateSellingPlan();
        this.updateSubscriptionDetails();
        this.updateStickyPrice();
      }

      updateSellingPlan() {
        const key = this.currentKey;
        const productData = this.config.products[key];
        const buyPanel = this.querySelector(
          `.combined-product__buy-panel[data-product-key="${key}"]`
        );
        if (!buyPanel) return;

        const sellingPlanInput = buyPanel.querySelector(
          'input[name="selling_plan"]'
        );
        if (!sellingPlanInput) return;

        if (
          this.currentPlan === 'subscribe' &&
          productData.hasSellingPlan
        ) {
          // Use the delivery dropdown's selected plan for current fragrance
          const data = this.getDeliveryData();
          sellingPlanInput.value = data ? data.planId : productData.sellingPlan;
          sellingPlanInput.disabled = false;
        } else {
          sellingPlanInput.value = '';
          sellingPlanInput.disabled = true;
        }
      }

      updateSubscriptionDetails() {
        const select = this.querySelector('.combined-product__delivery-dropdown');
        if (!select) return;

        const selectedOption = select.options[select.selectedIndex];
        if (!selectedOption) return;

        this.querySelectorAll('[data-subscription-plan-description]').forEach(
          (el) => {
            const description = selectedOption.dataset.planDescription || '';
            if (description) {
              el.textContent = description;
              el.hidden = false;
            } else {
              el.textContent = '';
              el.hidden = true;
            }
          }
        );
      }
  
      updateURL() {
        if (!window.location.pathname.startsWith('/products/')) return;
        if (!this.shouldSyncURL) return;
        const productData = this.config.products[this.currentKey];
        if (!productData?.url) return;
 
        const url = new URL(window.location.href);
        const productUrl = new URL(productData.url, window.location.origin);
        url.pathname = productUrl.pathname;
        window.history.replaceState({}, '', url.toString());
      }

      initInfoModal(type) {
        const modal = this.querySelector(`[data-${type}-modal]`);
        if (!modal) return;
        this.querySelectorAll(`[data-${type}-modal-open]`).forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.openInfoModal(modal);
          });
        });
        modal.querySelectorAll(`[data-${type}-modal-close]`).forEach((el) => {
          el.addEventListener('click', () => this.closeInfoModal(modal));
        });
      }

      openInfoModal(modal) {
        if (!modal) return;
        modal.hidden = false;
        document.body.style.overflow = 'hidden';
        modal.querySelector('.info-modal__close')?.focus();
      }

      closeInfoModal(modal) {
        if (!modal) return;
        modal.hidden = true;
        document.body.style.overflow = '';
      }

      closeAllModals() {
        this.querySelectorAll('.info-modal').forEach((modal) => {
          this.closeInfoModal(modal);
        });
      }

      onReadMoreToggle(e) {
        const btn = e.currentTarget;
        const wrapper = btn.closest('[data-product-key]') || btn.parentElement;
        const descriptionEl = wrapper.querySelector('.product__description');
        if (!descriptionEl) return;

        const isExpanded = descriptionEl.classList.contains(
          'combined-product__description--expanded'
        );

        if (isExpanded) {
          descriptionEl.classList.remove('combined-product__description--expanded');
          descriptionEl.classList.add('combined-product__description--truncated');
          btn.textContent = 'Read more';
        } else {
          descriptionEl.classList.add('combined-product__description--expanded');
          btn.textContent = 'Read less';
        }
      }

      reinitGallery(key) {
        const panel = this.querySelector(
          `.combined-product__media-panel[data-product-key="${key}"]`
        );
        if (!panel) return;

        const slider = panel.querySelector('media-gallery-slider');
        if (slider && typeof slider.swiperInitialization === 'function') {
          slider.previousWidth = 0;
          slider.swiperInitialization();
        }

        // Also try re-initializing the media-gallery custom element
        const gallery = panel.querySelector('media-gallery');
        if (gallery) {
          const swiperEl = gallery.querySelector('.swiper');
          if (swiperEl && swiperEl.swiper) {
            swiperEl.swiper.update();
          }
        }
      }

      /* ========================================
         Sticky Add to Cart Bar
         ======================================== */
      initStickyATC() {
        this.stickyBar = this.querySelector('[data-sticky-atc]');
        if (!this.stickyBar) return;

        this.stickySize = this.stickyBar.querySelector('[data-sticky-size]');
        this.stickyFragrance = this.stickyBar.querySelector('[data-sticky-fragrance]');
        this.stickyPlan = this.stickyBar.querySelector('[data-sticky-plan]');
        this.stickyPrice = this.stickyBar.querySelector('[data-sticky-price]');
        this.stickyBtn = this.stickyBar.querySelector('[data-sticky-atc-btn]');

        // Set initial dropdown values
        this.syncStickyDropdowns();
        this.updateStickyPrice();

        // Bind dropdown changes
        if (this.stickySize) {
          this.stickySize.addEventListener('change', (e) => {
            const newSize = e.target.value;
            if (newSize === this.currentSize) return;
            const previousHeight = this.offsetHeight;
            this.currentSize = newSize;
            if (newSize === '20ml') {
              this.currentPlan = 'onetime';
              this.updatePlanUI('onetime');
            }
            this.updatePills('size', newSize);
            this.updateView();
            this.emitCombinedVariantLayoutChange(previousHeight);
            this.syncStickyDropdowns();
            this.updateStickyPrice();
          });
        }

        if (this.stickyFragrance) {
          this.stickyFragrance.addEventListener('change', (e) => {
            const newFragrance = e.target.value;
            if (newFragrance === this.currentFragrance) return;
            const previousHeight = this.offsetHeight;
            this.currentFragrance = newFragrance;
            this.updatePills('fragrance', newFragrance);
            this.updateView();
            this.emitCombinedVariantLayoutChange(previousHeight);
            this.syncStickyDropdowns();
            this.updateStickyPrice();
          });
        }

        if (this.stickyPlan) {
          this.stickyPlan.addEventListener('change', (e) => {
            const nextPlan = e.target.value;
            const productData = this.config.products[this.currentKey];
            const hasSellingPlan = !!productData?.hasSellingPlan;

            if (nextPlan === 'subscribe' && !hasSellingPlan) {
              this.currentPlan = 'onetime';
            } else {
              this.currentPlan = nextPlan;
            }

            this.updatePlanUI(this.currentPlan);
            this.updateSellingPlan();
            this.updateSubscriptionDetails();
            this.syncStickyPlanState();
            this.updateStickyPrice();
          });
        }

        // Bind ATC button
        if (this.stickyBtn) {
          this.stickyBtn.addEventListener('click', () => this.onStickyAddToCart());
        }

        // Set up IntersectionObserver on the active buy panel
        this.initStickyObserver();
      }

      initStickyObserver() {
        if (!this.stickyBar) return;

        // Disconnect existing observer
        if (this.stickyObserver) {
          this.stickyObserver.disconnect();
        }

        this.stickyObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.stickyBar.classList.remove('sticky-atc--visible');
              } else {
                this.stickyBar.classList.add('sticky-atc--visible');
              }
            });
          },
          { threshold: 0, rootMargin: '0px' }
        );

        // Observe the currently visible buy panel's submit button
        this.observeActiveBuyButton();
      }

      observeActiveBuyButton() {
        if (!this.stickyObserver) return;
        this.stickyObserver.disconnect();

        const key = this.currentKey;
        const buyPanel = this.querySelector(
          `.combined-product__buy-panel[data-product-key="${key}"]`
        );
        if (!buyPanel) return;

        const submitBtn = buyPanel.querySelector('.product-form__submit');
        if (submitBtn) {
          this.stickyObserver.observe(submitBtn);
        }
      }

      syncStickyDropdowns() {
        if (this.stickySize) {
          this.stickySize.value = this.currentSize;
        }
        if (this.stickyFragrance) {
          this.stickyFragrance.value = this.currentFragrance;
        }
        this.syncStickyPlanState();
      }

      syncStickyPlanState() {
        if (!this.stickyPlan) return;
        const productData = this.config.products[this.currentKey];
        const hasSellingPlan = !!productData?.hasSellingPlan;
        const subscribeOption = this.stickyPlan.querySelector(
          'option[value="subscribe"]'
        );

        if (subscribeOption) {
          subscribeOption.disabled = !hasSellingPlan;
          subscribeOption.hidden = !hasSellingPlan;
        }

        if (!hasSellingPlan && this.currentPlan === 'subscribe') {
          this.currentPlan = 'onetime';
          this.updatePlanUI('onetime');
          this.updateSellingPlan();
          this.updateSubscriptionDetails();
        }

        this.stickyPlan.value =
          this.currentPlan === 'subscribe' && hasSellingPlan
            ? 'subscribe'
            : 'onetime';
      }

      updateStickyPrice() {
        if (!this.stickyPrice) return;
        const key = this.currentKey;
        const productData = this.config.products[key];
        if (!productData) return;

        let price = productData.price;
        if (this.currentPlan === 'subscribe' && productData.hasSellingPlan) {
          const deliveryData = this.getDeliveryData();
          if (deliveryData && Number.isFinite(deliveryData.price)) {
            price = deliveryData.price;
          }
        }
        // Format as money (price is in cents)
        const formatted = (price / 100).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
        });
        this.stickyPrice.textContent = 'Total: ' + formatted;

        // Update button disabled state
        if (this.stickyBtn) {
          this.stickyBtn.disabled = !productData.available;
          this.stickyBtn.textContent = productData.available
            ? 'Add to Cart'
            : 'Sold Out';
        }
      }

      onStickyAddToCart() {
        const key = this.currentKey;
        const buyPanel = this.querySelector(
          `.combined-product__buy-panel[data-product-key="${key}"]`
        );
        if (!buyPanel) return;

        const form = buyPanel.querySelector('form[data-type="add-to-cart-form"]');
        if (!form) return;

        const submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn && !submitBtn.disabled) {
          // Show loading state on sticky button
          if (this.stickyBtn) {
            this.stickyBtn.classList.add('sticky-atc__button--loading');
            this.stickyBtn.textContent = 'Adding...';
          }
          submitBtn.click();
          // Reset after a delay (the form handler manages the actual state)
          setTimeout(() => {
            if (this.stickyBtn) {
              this.stickyBtn.classList.remove('sticky-atc__button--loading');
              this.stickyBtn.textContent = 'Add to Cart';
            }
          }, 2000);
        }
      }
    }
  );
}
